import { Link } from '@tanstack/react-router';
import { ArrowLeft, Trophy, Medal } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatCurrency } from '@/lib/utils';

interface LeaderboardEntry {
  rank: number;
  name: string;
  avatarUrl: string | null;
  revenueSourced: number;
  dealsWon: number;
  pipelineValue: number;
}

export function LeaderboardPage() {
  // Placeholder - will be wired to trpc.reports.leaderboard
  const entries: LeaderboardEntry[] = [];

  const getRankIcon = (rank: number) => {
    if (rank === 1)
      return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2)
      return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3)
      return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/reports" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leaderboard</h1>
          <p className="text-muted-foreground">Team rankings this quarter.</p>
        </div>
      </div>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No data available yet. Close some deals to see the leaderboard.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <Card key={entry.rank}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex h-10 w-10 items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>

                <Avatar className="h-10 w-10">
                  <AvatarImage src={entry.avatarUrl ?? undefined} />
                  <AvatarFallback>
                    {entry.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1">
                  <p className="font-semibold">{entry.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {entry.dealsWon} deals won
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-semibold">
                    {formatCurrency(entry.revenueSourced, 'CAD')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatCurrency(entry.pipelineValue, 'CAD')} pipeline
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
